import React, { useState, useEffect } from "react";
import {
  Button,
  Grid,
  TextField,
  Typography,
  Box,
  useTheme,
} from "@mui/material";
import { useDepthChart } from "../../../../contexts/DepthChartContext";
import { useLogger } from "../../../../contexts/LoggerContext";
import { PlayerSelection } from "../Components/PlayerSelection";
import { RosterPlayer } from "../../../../types/LogTypes";

export const FieldGoalKickLog = ({
  kickType,
  setKickType,
}: {
  kickType: "PAT" | "FG";
  setKickType: (kickType: "PAT" | "FG") => void;
}) => {
  const { fieldGoalKickers } = useDepthChart();
  const {
    setCurrentPlayLog,
    gameSituation,
    setGameSituation,
    clearSelections,
  } = useLogger();
  const [selectedKicker, setSelectedKicker] = useState<RosterPlayer | null>(
    null
  );
  const [jerseyNumber, setJerseyNumber] = useState<string>("");
  const [fieldGoalDistance, setFieldGoalDistance] = useState<number>(
    gameSituation.yardline + 7
  );
  const [fieldGoalResult, setFieldGoalResult] = useState<
    "GOOD" | "MISSED" | "BLOCKED" | null
  >(null);

  const theme = useTheme();

  const handleJerseyNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    if (value === "" || (parseInt(value) >= 0 && parseInt(value) <= 99)) {
      setJerseyNumber(value);
    }
  };

  const handleFieldGoalDistanceChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    if (value === "" || (parseInt(value) >= 0 && parseInt(value) <= 99)) {
      setFieldGoalDistance(parseInt(value) || 0);
    }
  };

  const handleKickerSelection = (player: RosterPlayer) => {
    setSelectedKicker(player);
    setJerseyNumber(player.jersey_number?.toString() || "");
  };

  useEffect(() => {
    if (selectedKicker && fieldGoalResult) {
      setCurrentPlayLog({
        kicker_roster_player: selectedKicker,
        field_goal_distance: fieldGoalDistance,
        field_goal_result: fieldGoalResult,
        pat: kickType === "PAT",
      });
    }
  }, [selectedKicker, fieldGoalDistance, fieldGoalResult]);

  useEffect(() => {
    if (clearSelections) {
      setSelectedKicker(null);
      setJerseyNumber("");
      setFieldGoalDistance(gameSituation.yardline + 7);
      setFieldGoalResult(null);
    }
  }, [clearSelections]);
  return (
    <Box>
      <Grid container>
        {/* Kicker */}
        <Grid item xs={2}>
          <PlayerSelection
            title="Kicker"
            players={fieldGoalKickers}
            selectedPlayer={selectedKicker}
            jerseyNumber={jerseyNumber}
            maxDisplayedPlayers={1}
            handlePlayerSelection={handleKickerSelection}
            handleJerseyNumberChange={handleJerseyNumberChange}
          />
        </Grid>

        {/* Kick Log */}
        <Grid item xs={8}>
          <Box sx={{ flex: 1 }}></Box>

          {/* Field Goal Distance */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <TextField
              type="number"
              label="Field Goal Distance"
              value={fieldGoalDistance}
              onChange={handleFieldGoalDistanceChange}
              inputProps={{
                min: 0,
                max: 99,
                step: 1,
                style: { textAlign: "center" },
              }}
              sx={{
                width: 200,
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                  {
                    opacity: 1,
                    margin: 0,
                    marginLeft: "8px",
                  },
              }}
            />
          </Box>

          {/* Field Goal Result */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              maxWidth: 600,
              mt: 2,
            }}
          >
            <Button
              variant={fieldGoalResult === "GOOD" ? "contained" : "outlined"}
              color={fieldGoalResult === "GOOD" ? "success" : "primary"}
              sx={{
                minWidth: 150,
                fontWeight: fieldGoalResult === "GOOD" ? "bold" : "normal",
              }}
              onClick={() => {
                setFieldGoalResult("GOOD");
                setGameSituation({
                  ...gameSituation,
                  club_score:
                    gameSituation.club_score + (kickType === "PAT" ? 1 : 3),
                });
              }}
            >
              Good
            </Button>
            <Button
              variant={fieldGoalResult === "MISSED" ? "contained" : "outlined"}
              color={fieldGoalResult === "MISSED" ? "error" : "primary"}
              sx={{
                minWidth: 150,
                fontWeight: fieldGoalResult === "MISSED" ? "bold" : "normal",
              }}
              onClick={() => setFieldGoalResult("MISSED")}
            >
              Missed
            </Button>
            <Button
              variant={fieldGoalResult === "BLOCKED" ? "contained" : "outlined"}
              color={fieldGoalResult === "BLOCKED" ? "error" : "primary"}
              sx={{
                minWidth: 150,
                fontWeight: fieldGoalResult === "BLOCKED" ? "bold" : "normal",
              }}
              onClick={() => setFieldGoalResult("BLOCKED")}
            >
              Blocked
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
